import type { DropdownState, FocusedElement, InstrumentType } from '../state/types';
import { ARIA_LABELS } from '../types';
import {
    SymbolPickerMenu,
    SymbolPickerMenuTrigger,
    SymbolPickerMenuContent,
    SymbolPickerMenuList,
    SymbolPickerMenuItem,
} from './SymbolPickerMenu';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Badge } from 'src/components/ui/badges';
import { useEvent } from 'src/hooks/useEvent';

// Custom checkmark item component
const CheckmarkItem = ({
    checked,
    selected,
    onSelect,
    onMouseEnter,
    children,
}: {
    checked: boolean;
    selected: boolean;
    onSelect: () => void;
    onMouseEnter: () => void;
    children: React.ReactNode;
}) => (
    <SymbolPickerMenuItem
        selected={selected}
        onSelect={onSelect}
        onMouseEnter={onMouseEnter}
        className="flex h-6 w-full cursor-pointer items-center gap-2 px-2 py-1 text-xs hover:bg-gray-600"
    >
        <div className="flex w-4 justify-center">{checked && <FontAwesomeIcon icon={faCheck} size="sm" />}</div>
        <span>{children}</span>
    </SymbolPickerMenuItem>
);

export interface InstrumentTypeToggleProps {
    allowedInstrumentTypes: InstrumentType[];
    instrumentType: InstrumentType;
    onChange: (newType: InstrumentType) => void;
    // State machine props
    dropdownOpen: boolean;
    dropdown: DropdownState;
    onDropdownActiveIndex: (index: number) => void;
    onFocus: (element: FocusedElement) => void;
}

export const InstrumentTypeToggle = ({
    allowedInstrumentTypes,
    instrumentType,
    onChange,
    dropdownOpen,
    dropdown,
    onDropdownActiveIndex,
    onFocus,
}: InstrumentTypeToggleProps) => {
    const handleInputClick = useEvent((e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        onFocus('instrumentType');
    });

    const handleInputFocus = useEvent((e: React.FocusEvent) => {
        e.preventDefault();
        e.stopPropagation();
    });

    const handleInputBlur = useEvent((e: React.FocusEvent) => {
        e.preventDefault();
    });

    const handleDropdownActiveIndex = useEvent((index: number) => {
        onDropdownActiveIndex(index);
    });

    const handleTypeSelect = useEvent((type: InstrumentType) => {
        onChange(type);
    });

    // Return null if less than 2 instrument types are allowed
    if (allowedInstrumentTypes.length < 2) {
        return null;
    }

    return (
        <SymbolPickerMenu open={dropdownOpen}>
            <SymbolPickerMenuTrigger>
                <Badge
                    as="button"
                    tabIndex={-1}
                    aria-label={ARIA_LABELS.instrumentType}
                    variant="bordered"
                    fill="light"
                    color={instrumentType === 'equity' ? 'green' : 'yellow'}
                    className="h-4 text-xs"
                    onClick={handleInputClick}
                    onFocus={handleInputFocus}
                    onBlur={handleInputBlur}
                    aria-pressed={instrumentType === 'options'}
                >
                    {instrumentType === 'equity' ? 'Equities' : 'Options'}
                </Badge>
            </SymbolPickerMenuTrigger>
            <SymbolPickerMenuContent className="w-24 p-1">
                <SymbolPickerMenuList>
                    {allowedInstrumentTypes.map((type, index) => (
                        <CheckmarkItem
                            key={type}
                            checked={instrumentType === type}
                            selected={dropdown.activeIndex === index}
                            onSelect={() => handleTypeSelect(type)}
                            onMouseEnter={() => handleDropdownActiveIndex(index)}
                        >
                            {type === 'equity' ? 'Equity' : 'Options'}
                        </CheckmarkItem>
                    ))}
                </SymbolPickerMenuList>
            </SymbolPickerMenuContent>
        </SymbolPickerMenu>
    );
};
